import type { ReactNode } from "react";
import { Building2, Copy, Link as LinkIcon, Save } from "lucide-react";
import { toast } from "sonner";
import type { Company, Lab } from "@/lib/api_client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { CompanyQR } from "@/components/CompanyQR";
import { cn } from "@/lib/cn";
import {
  type CompanyFormState,
  type CompanyFlowType,
  type FieldErrors,
  flowTypeLabel,
  initials,
  loginUrl,
  qrFileName,
  requiresInternalLab,
  usesInternalLab,
} from "./company-form";

export type PanelTab = "grunninfo" | "innlogging";

const TABS: { id: PanelTab; label: string }[] = [
  { id: "grunninfo", label: "Grunninfo" },
  { id: "innlogging", label: "Innlogging & QR" },
];

interface CompanyPanelProps {
  company: Company | null;
  form: CompanyFormState;
  errors: FieldErrors;
  labs: Lab[];
  tab: PanelTab;
  onTabChange: (tab: PanelTab) => void;
  onChange: <K extends keyof CompanyFormState>(
    field: K,
    value: CompanyFormState[K],
  ) => void;
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
  pendingName: string | null;
  onDiscardPending: () => void;
  onSavePending: () => void;
  /** BHT staff read companies but cannot write them. */
  readOnly: boolean;
}

export function CompanyPanel({
  company,
  form,
  errors,
  labs,
  tab,
  onTabChange,
  onChange,
  dirty,
  saving,
  onSave,
  onCancel,
  pendingName,
  onDiscardPending,
  onSavePending,
  readOnly,
}: CompanyPanelProps) {
  const errorCount = Object.keys(errors).length;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="px-6 pt-5">
        <div className="flex items-center gap-3">
          {form.logoUrl ? (
            <img
              src={form.logoUrl}
              alt=""
              className="h-9 w-9 rounded-lg bg-white object-contain p-0.5"
            />
          ) : (
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-admin-card text-sm font-semibold text-admin-primary">
              {company ? initials(company.name) : "+"}
            </span>
          )}
          <div className="min-w-0">
            <h3 className="truncate text-lg font-semibold">
              {form.name || (company ? company.name : "Ny bedrift")}
            </h3>
            <p className="font-mono text-xs text-admin-text-subtle">
              {company ? company.slug : "Lagres med en generert lenke"}
            </p>
          </div>
          <Badge variant="secondary">{flowTypeLabel(form.flowType)}</Badge>
        </div>

        {/* Rule 3: over ~8 fields the panel gets tabs, Grunninfo always first. */}
        <div className="mt-4 inline-flex gap-1 rounded-lg bg-admin-card p-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => onTabChange(t.id)}
              className={cn(
                "cursor-pointer rounded-md px-3 py-1 text-sm font-medium transition-colors",
                tab === t.id
                  ? "bg-admin-card-header text-admin-text-primary"
                  : "text-admin-text-subtle hover:text-admin-text-secondary",
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <fieldset
        disabled={readOnly}
        className="min-h-0 min-w-0 flex-1 space-y-4 overflow-auto px-6 py-4"
      >
        {tab === "grunninfo" ? (
          <Section title="Grunnleggende informasjon">
            <Field
              id="company-name"
              label="Navn"
              error={errors.name}
              className="sm:col-span-2"
            >
              <Input
                id="company-name"
                value={form.name}
                onChange={(e) => onChange("name", e.target.value)}
                placeholder="Acme AS"
              />
            </Field>
            <Field id="company-flow-type" label="Flyt">
              <Select
                id="company-flow-type"
                value={form.flowType}
                onChange={(e) => {
                  const nextFlow = e.target.value as CompanyFlowType;
                  onChange("flowType", nextFlow);
                  if (nextFlow === "consumer") onChange("labId", "");
                }}
              >
                <option value="healthCheck">
                  {flowTypeLabel("healthCheck")}
                </option>
                <option value="consumer">{flowTypeLabel("consumer")}</option>
                <option value="drDropinBht">
                  {flowTypeLabel("drDropinBht")}
                </option>
                <option value="kickstart">{flowTypeLabel("kickstart")}</option>
              </Select>
            </Field>
            {usesInternalLab(form.flowType) && (
              <Field
                id="company-lab"
                label={
                  requiresInternalLab(form.flowType)
                    ? "Lab (intern, påkrevd)"
                    : "Lab (intern, valgfritt)"
                }
                error={errors.labId}
              >
                <Select
                  id="company-lab"
                  value={form.labId}
                  onChange={(e) => onChange("labId", e.target.value)}
                >
                  <option value="">
                    {requiresInternalLab(form.flowType)
                      ? "Velg lab…"
                      : "Ingen — ansatte velger fra offentlige labber"}
                  </option>
                  {labs
                    .filter((lab) => !lab.public)
                    .map((lab) => (
                      <option key={lab.id} value={lab.id}>
                        {lab.name}
                      </option>
                    ))}
                </Select>
              </Field>
            )}
            <Field
              id="company-income-per-employee"
              label="Inntekt per ansatt (kr/år)"
              error={errors.incomePerEmployee}
              hint="Inntekten vi fakturerer per ansatt per år, før kostnader. Delt på blodprøver per år gir den hva én helsesjekk er verdt, som er tallet Lønnsomhet-dashboardet regner margin på."
            >
              <Input
                id="company-income-per-employee"
                inputMode="decimal"
                value={form.incomePerEmployee}
                onChange={(e) => onChange("incomePerEmployee", e.target.value)}
                placeholder="f.eks. 1490"
              />
            </Field>
            <Field id="company-visits-per-year" label="Blodprøver per år">
              <Select
                id="company-visits-per-year"
                value={String(form.visitsPerYear)}
                onChange={(e) =>
                  onChange("visitsPerYear", Number(e.target.value))
                }
              >
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
              </Select>
            </Field>
            <Field
              id="company-allow-blood-test-request"
              label="Tillat bestilling av blodprøveforslag"
              hint="På: kunden kan selv be om blodprøveforslaget. Av: bedriften bestiller blodprøven, og kunden kan ikke bestille selv."
              className="sm:col-span-2"
            >
              <div className="flex h-9 items-center gap-2">
                <input
                  id="company-allow-blood-test-request"
                  type="checkbox"
                  checked={form.allowBloodTestRequest}
                  onChange={(e) =>
                    onChange("allowBloodTestRequest", e.target.checked)
                  }
                  className="h-4 w-4 accent-admin-primary"
                />
                <span className="text-sm text-admin-text-secondary">
                  {form.allowBloodTestRequest ? "På" : "Av"}
                </span>
              </div>
            </Field>
            <Field id="company-deactivated-at" label="Deaktiveres fra">
              <Input
                id="company-deactivated-at"
                type="datetime-local"
                value={form.deactivatedAt}
                onChange={(e) => onChange("deactivatedAt", e.target.value)}
              />
              {form.deactivatedAt && (
                <button
                  onClick={() => onChange("deactivatedAt", "")}
                  className="mt-1 cursor-pointer text-xs text-admin-info hover:underline"
                >
                  Fjern deaktivering
                </button>
              )}
            </Field>
            <Field
              id="company-logo-url"
              label="Logo-URL"
              className="sm:col-span-2"
            >
              <Input
                id="company-logo-url"
                value={form.logoUrl}
                onChange={(e) => onChange("logoUrl", e.target.value)}
                placeholder="https://..."
              />
            </Field>
          </Section>
        ) : (
          <Section title="Innlogging & QR" columns={1}>
            {company ? (
              <>
                <Field id="company-login-url" label="Innloggingslenke">
                  <div className="flex items-center gap-2">
                    <Input
                      id="company-login-url"
                      value={loginUrl(company.slug)}
                      readOnly
                      className="text-admin-text-subtle"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copy(loginUrl(company.slug), "Innloggingslenke kopiert")}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </Field>
                {/* Rule 8: the QR is derived from the slug — panel, never a column. */}
                <div className="flex justify-center rounded-md bg-admin-bg py-4">
                  <CompanyQR
                    url={loginUrl(company.slug)}
                    fileName={qrFileName(company.name, company.slug)}
                  />
                </div>
              </>
            ) : (
              <p className="text-sm text-admin-text-subtle">
                Innloggingslenken og QR-koden finnes når bedriften er opprettet.
              </p>
            )}
            <Field id="company-booking-url" label="Bookinglenke">
              <div className="flex items-center gap-2">
                <Input
                  id="company-booking-url"
                  value={form.bookingUrl}
                  onChange={(e) => onChange("bookingUrl", e.target.value)}
                  placeholder="https://..."
                />
                {form.bookingUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copy(form.bookingUrl, "Bookinglenke kopiert")}
                  >
                    <LinkIcon className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </Field>
          </Section>
        )}
      </fieldset>

      {/* Rule 5: a record with unsaved changes never loses them in silence. */}
      {pendingName && (
        <div className="flex items-center gap-3 border-t border-admin-warn-400/40 bg-admin-warn-400/10 px-6 py-3">
          <span className="text-sm text-admin-warn-400">
            Ulagrede endringer. Du er på vei til {pendingName}.
          </span>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={onDiscardPending}>
            Forkast
          </Button>
          <Button size="sm" onClick={onSavePending} disabled={saving}>
            Lagre og bytt
          </Button>
        </div>
      )}

      {/* Rule 4: saving is explicit and lives at the bottom of the panel. */}
      {!readOnly && (
        <div className="flex items-center gap-3 border-t border-admin-card-border bg-admin-card/40 px-6 py-3">
          <span
            className={cn(
              "text-sm",
              errorCount > 0
                ? "text-admin-negative"
                : dirty
                  ? "text-admin-warn-400"
                  : "text-admin-text-subtle",
            )}
          >
            {errorCount > 0
              ? `${errorCount} felt må rettes`
              : dirty
                ? "Ulagrede endringer"
                : company
                  ? "Ingen endringer"
                  : "Ny bedrift"}
          </span>
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={!dirty || saving}
          >
            Avbryt
          </Button>
          <Button size="sm" onClick={onSave} disabled={!dirty || saving}>
            <Save className="h-3 w-3" />
            {company ? "Lagre endringer" : "Opprett bedrift"}
          </Button>
        </div>
      )}
    </div>
  );
}

export function EmptyPanel() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 p-12 text-center">
      <Building2 className="h-6 w-6 text-admin-text-subtle" />
      <p className="text-sm text-admin-text-subtle">
        Velg en bedrift i listen, eller opprett en ny.
      </p>
    </div>
  );
}

function copy(value: string, message: string) {
  navigator.clipboard.writeText(value);
  toast.success(message);
}

function Section({
  title,
  columns = 2,
  children,
}: {
  title: string;
  columns?: 1 | 2;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-admin-card-border bg-admin-card/40 p-5">
      <h4 className="mb-4 text-sm font-semibold">{title}</h4>
      <div
        className={cn(
          "grid gap-4",
          columns === 2 ? "sm:grid-cols-2" : "grid-cols-1",
        )}
      >
        {children}
      </div>
    </div>
  );
}

/** `id` is required: it binds the label to its control, so clicking the label
 *  focuses the field and screen readers read the two together. */
function Field({
  id,
  label,
  error,
  hint,
  className,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className}>
      <label
        htmlFor={id}
        className="mb-1 block text-xs font-medium text-admin-text-subtle"
      >
        {label}
      </label>
      {children}
      {/* Rule 10: the error sits at the field, before the call is sent. */}
      {error ? (
        <p className="mt-1 text-xs text-admin-negative">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-admin-text-subtle">{hint}</p>
      ) : null}
    </div>
  );
}
